import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Layers,
  Calendar,
  ChefHat
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const CustomRotations = () => {
  const [rotations, setRotations] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRotation, setEditingRotation] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slot_name: '',
    rotation_length: 1,
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    schedule: {},
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rotationsRes, recipesRes] = await Promise.all([
        axios.get(`${API}/rotations/custom`),
        axios.get(`${API}/recipes`)
      ]);
      setRotations(rotationsRes.data);
      setRecipes(recipesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRotation(null);
    setFormData({
      name: '',
      slot_name: '',
      rotation_length: 1,
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      schedule: {},
      active: true
    });
    setShowDialog(true);
  };

  const openEditDialog = (rotation) => {
    setEditingRotation(rotation);
    setFormData({
      name: rotation.name,
      slot_name: rotation.slot_name,
      rotation_length: rotation.rotation_length,
      days: rotation.days,
      schedule: rotation.schedule || {},
      active: rotation.active
    });
    setShowDialog(true);
  };

  const toggleDay = (day) => {
    const newDays = formData.days.includes(day)
      ? formData.days.filter(d => d !== day)
      : [...formData.days, day];
    setFormData({ ...formData, days: newDays });
  };

  const updateSchedule = (week, day, recipe) => {
    const newSchedule = { ...formData.schedule };
    if (!newSchedule[week]) {
      newSchedule[week] = {};
    }
    newSchedule[week][day] = recipe === '__none__' ? '' : recipe;
    setFormData({ ...formData, schedule: newSchedule });
  };

  const getScheduleValue = (week, day) => {
    return formData.schedule?.[week]?.[day] || '';
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a rotation name');
      return;
    }
    if (!formData.slot_name.trim()) {
      toast.error('Please enter a slot name (e.g., dessert, drink)');
      return;
    }
    if (formData.days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    setSaving(true);
    try {
      if (editingRotation) {
        await axios.put(`${API}/rotations/custom/${editingRotation.id}`, formData);
        toast.success('Rotation updated successfully');
      } else {
        await axios.post(`${API}/rotations/custom`, formData);
        toast.success('Rotation created successfully');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rotation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rotation) => {
    if (!window.confirm(`Delete "${rotation.name}" rotation?`)) return;
    
    try {
      await axios.delete(`${API}/rotations/custom/${rotation.id}`);
      toast.success('Rotation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rotation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-rotations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight">
            Custom Rotations
          </h1>
          <p className="text-stone-500 mt-1">Create rotations for desserts, drinks, snacks, or any custom items</p>
        </div>
        <button
          onClick={openCreateDialog}
          className="btn-primary"
          data-testid="create-rotation-btn"
        >
          <Plus size={20} />
          <span>New Rotation</span>
        </button>
      </div>

      {/* Rotations Grid */}
      {rotations.length === 0 ? (
        <div className="virsa-card p-12 text-center">
          <Layers size={48} className="mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-600 mb-2">No Custom Rotations Yet</h3>
          <p className="text-stone-500 mb-4">
            Create rotations for items like desserts, drinks, or snacks that will be combined with your main menu on the calendar.
          </p>
          <button onClick={openCreateDialog} className="btn-primary">
            <Plus size={18} />
            <span>Create Your First Rotation</span>
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rotations.map(rotation => (
            <div 
              key={rotation.id} 
              className={`virsa-card p-5 ${!rotation.active ? 'opacity-60' : ''}`}
              data-testid={`rotation-card-${rotation.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#124242]/10 flex items-center justify-center">
                    <Layers size={20} className="text-[#124242]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-800">{rotation.name}</h3>
                    <p className="text-sm text-stone-500">Slot: {rotation.slot_name}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditDialog(rotation)}
                    className="p-2 text-stone-500 hover:bg-stone-100 rounded-lg transition-colors"
                    data-testid={`edit-rotation-${rotation.id}`}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(rotation)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-rotation-${rotation.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Calendar size={14} />
                  <span>{rotation.days?.join(', ') || 'No days selected'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <ChefHat size={14} />
                  <span>{rotation.rotation_length} week{rotation.rotation_length > 1 ? 's' : ''} rotation</span>
                </div>
              </div>

              {!rotation.active && (
                <div className="mt-3 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded inline-block">
                  Inactive
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingRotation ? 'Edit Rotation' : 'Create New Rotation'}
            </DialogTitle>
            <DialogDescription>
              {editingRotation 
                ? 'Update the rotation settings and schedule' 
                : 'Create a custom rotation for items like desserts, drinks, or snacks'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-2">
                  Rotation Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="virsa-input"
                  placeholder="e.g., Dessert Rotation"
                  data-testid="rotation-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-600 mb-2">
                  Slot Name *
                </label>
                <input
                  type="text"
                  value={formData.slot_name}
                  onChange={(e) => setFormData({ ...formData, slot_name: e.target.value.toLowerCase() })}
                  className="virsa-input"
                  placeholder="e.g., dessert, drink, snack"
                  data-testid="slot-name-input"
                />
                <p className="text-xs text-stone-500 mt-1">This will appear as a column on the calendar</p>
              </div>
            </div>

            {/* Rotation Length */}
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                Rotation Length
              </label>
              <Select
                value={formData.rotation_length.toString()}
                onValueChange={(v) => setFormData({ ...formData, rotation_length: parseInt(v) })}
              >
                <SelectTrigger className="w-full md:w-48 h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} Week{n > 1 ? 's' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Days Selection */}
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                Active Days *
              </label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.days.includes(day)
                        ? 'bg-[#124242] text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                    data-testid={`day-toggle-${day}`}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="active-toggle"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-5 h-5 rounded border-stone-300 text-[#124242] focus:ring-[#124242]"
              />
              <label htmlFor="active-toggle" className="text-sm font-medium text-stone-600">
                Active (show on calendar)
              </label>
            </div>

            {/* Schedule */}
            <div>
              <h3 className="text-lg font-semibold text-stone-800 mb-4">Schedule</h3>
              {Array.from({ length: formData.rotation_length }, (_, weekIdx) => {
                const weekKey = `Week${weekIdx + 1}`;
                return (
                  <div key={weekKey} className="mb-6">
                    <h4 className="text-sm font-semibold text-stone-600 mb-3 uppercase tracking-wide">
                      {weekKey}
                    </h4>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {formData.days.sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b)).map(day => (
                        <div key={`${weekKey}-${day}`} className="flex items-center gap-2">
                          <span className="text-sm font-medium text-stone-600 w-12">{day.slice(0, 3)}</span>
                          <Select
                            value={getScheduleValue(weekKey, day) || '__none__'}
                            onValueChange={(v) => updateSchedule(weekKey, day, v)}
                          >
                            <SelectTrigger className="flex-1 h-10">
                              <SelectValue placeholder="Select recipe" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">None</SelectItem>
                              {recipes.map(r => (
                                <SelectItem key={r.id} value={r.recipe_name}>
                                  {r.recipe_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setShowDialog(false)} className="btn-outline">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              data-testid="save-rotation-btn"
            >
              {saving ? <span className="spinner" /> : <Save size={18} />}
              <span>{editingRotation ? 'Update' : 'Create'} Rotation</span>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
